import { all, call, fork, put, take } from "redux-saga/effects";
import { ClientBookmark } from "src/domain/model/client_bookmark";
import { Customer } from "src/domain/model/customer";
import { IClientBookmarkRepository } from "src/domain/repository/client_bookmark_repository";
import { ICustomerRepository } from "src/domain/repository/customer_repository";
import {
  IJobCareerRepository,
  TJobCareerWithCustomerID,
} from "src/domain/repository/job_career_repository";
import { IApiClient, createApiClient } from "src/infra/api/client";
import { createClientBookmarkAPI } from "src/infra/api/service/client_bookmark_api";
import { createCustomerAPI } from "src/infra/api/service/customer_api";
import { createJobCareerAPI } from "src/infra/api/service/job_career_api";
import {
  ClientSalesCampaignActionType,
  IClientSalesCampaignActionCreator,
  IRequestGetCustomerByClientSalesCampaignPageAction,
  createClientSalesCampaignActionCreator,
} from "../action/client_sales_campaign";
import { GetCustomersByClientSalesCampaignPageResponse } from "../response/customer_response";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const jobcareerRepository: IJobCareerRepository = createJobCareerAPI(apiClient);
const clientBookmarkRepository: IClientBookmarkRepository =
  createClientBookmarkAPI(apiClient);
const actionCreater: IClientSalesCampaignActionCreator =
  createClientSalesCampaignActionCreator();

function* handleGetCustomersInSalesCampaignPage() {
  while (true) {
    try {
      const action: IRequestGetCustomerByClientSalesCampaignPageAction =
        yield take(ClientSalesCampaignActionType.REQUEST_GET_CUSTOMERS);

      const [
        salesStrategyCustomers,
        cooCustomers,
        kpiCustomers,
        partnerSalesCustomers,
        allianceCustomers,
        salesOrgDevCustomers,
        sfaCrmCustomers,
        salesEnablementCustomers,
        salesDxCustomers,
        insideSalesCustomers,
        customerSuccessCustomers,
        ltvCustomers,
        groupSalesCustomers,
        interDeptCustomers,
        crossSellCustomers,
        clientBookmarks,
      ] = yield all([
        call(getCustomersByIDs, action.item.salesStrategyCustomerIDs),
        call(getCustomersByIDs, action.item.cooCustomerIDs),
        call(getCustomersByIDs, action.item.kpiCustomerIDs),
        call(getCustomersByIDs, action.item.partnerSalesCustomerIDs),
        call(getCustomersByIDs, action.item.allianceCustomerIDs),
        call(getCustomersByIDs, action.item.salesOrgDevCustomerIDs),
        call(getCustomersByIDs, action.item.sfaCrmCustomerIDs),
        call(getCustomersByIDs, action.item.salesEnablementCustomerIDs),
        call(getCustomersByIDs, action.item.salesDxCustomerIDs),
        call(getCustomersByIDs, action.item.insideSalesCustomerIDs),
        call(getCustomersByIDs, action.item.customerSuccessCustomerIDs),
        call(getCustomersByIDs, action.item.ltvCustomerIDs),
        call(getCustomersByIDs, action.item.groupSalesCustomerIDs),
        call(getCustomersByIDs, action.item.interDeptCustomerIDs),
        call(getCustomersByIDs, action.item.crossSellCustomerIDs),
        call(getAllClientBookmark),
      ]);

      let allCustomerList: Customer[] = [];
      allCustomerList = allCustomerList.concat(
        salesStrategyCustomers,
        cooCustomers,
        kpiCustomers,
        partnerSalesCustomers,
        allianceCustomers,
        salesOrgDevCustomers,
        sfaCrmCustomers,
        salesEnablementCustomers,
        salesDxCustomers,
        insideSalesCustomers,
        customerSuccessCustomers,
        ltvCustomers,
        groupSalesCustomers,
        interDeptCustomers,
        crossSellCustomers
      );
      const customerIdList: string[] = yield call(
        createCustomerIdList,
        allCustomerList
      );
      const jobCareersWithCustomerID: TJobCareerWithCustomerID[] = yield call(
        getJobcareerAllByCustomerIDs,
        customerIdList
      );
      const res: GetCustomersByClientSalesCampaignPageResponse =
        new GetCustomersByClientSalesCampaignPageResponse();
      res.salesStrategyCustomers = salesStrategyCustomers;
      res.cooCustomers = cooCustomers;
      res.kpiCustomers = kpiCustomers;
      res.partnerSalesCustomers = partnerSalesCustomers;
      res.allianceCustomers = allianceCustomers;
      res.salesOrgDevCustomers = salesOrgDevCustomers;
      res.sfaCrmCustomers = sfaCrmCustomers;
      res.salesEnablementCustomers = salesEnablementCustomers;
      res.salesDxCustomers = salesDxCustomers;
      res.insideSalesCustomers = insideSalesCustomers;
      res.customerSuccessCustomers = customerSuccessCustomers;
      res.ltvCustomers = ltvCustomers;
      res.groupSalesCustomers = groupSalesCustomers;
      res.interDeptCustomers = interDeptCustomers;
      res.crossSellCustomers = crossSellCustomers;
      res.jobCareersWithCustomerID = jobCareersWithCustomerID;
      res.clientBookmarks = clientBookmarks;

      yield put(
        actionCreater.callbackGetCustomerByClientSalesCampaignPageAction(true, res)
      );
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreater.callbackGetCustomerByClientSalesCampaignPageAction(
          false,
          undefined
        )
      );
    }
  }
}

const createCustomerIdList = (customerList: Customer[]): string[] => {
  return customerList.map((item: Customer) => {
    return item.id;
  });
};

// 指定したID順・件数どおりにCustomerを組み立てる。
// getAllByIDsはサーバ側でID重複が解決されるため、開発環境のように同一IDを
// 複数並べたケースでも、リクエスト件数分のカードを表示できるようにする。
const getCustomersByIDs = (ids: string[]): Promise<Customer[]> => {
  return customerRepository.getAllByIDs(ids).then((customers: Customer[]) => {
    return ids
      .map((id: string) => customers.find((c: Customer) => c.id === id))
      .filter((c: Customer | undefined): c is Customer => c !== undefined);
  });
};

const getJobcareerAllByCustomerIDs = (
  ids: string[]
): Promise<TJobCareerWithCustomerID[]> => {
  return jobcareerRepository.getAllByCustomerIDsForScout(ids);
};

const getAllClientBookmark = (): Promise<ClientBookmark[]> => {
  return clientBookmarkRepository.getAllByClient();
};

export { handleGetCustomersInSalesCampaignPage };



// WEBPACK FOOTER //
// ./src/presentation/handler/client_sales_campaign_handler.ts